import React, { useEffect, useRef, useState } from 'react';

/**
 * @param containerRef
 * @param contentRef
 * @returns {Element}
 * @constructor
 */
const CustomScrollbar = ({containerRef, contentRef}) => {
    const [isEnabled, setIsEnabled] = useState(false);
    const [thumbWidth, setThumbWidth] = useState(0);
    const [thumbPosition, setThumbPosition] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startDragX, setStartDragX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    /**
     * @type {React.MutableRefObject<HTMLDivElement|undefined>}
     */
    const thumbRef = useRef();

    /**
     * @type {React.MutableRefObject<number>}
     */
    const resizeTimeoutRef = useRef(0);

    const calculateThumbWidthAndPosition = () => {
        if (!isEnabled) return;

        const containerWidth = containerRef.current.clientWidth;
        const contentWidth = contentRef.current.scrollWidth;

        if (contentWidth <= containerWidth) {
            setIsEnabled(false);
            return;
        }

        const visibleRatio = containerWidth / contentWidth;
        const thumbWidth = Math.max(visibleRatio * 100, 5);
        const scrollRatio = scrollLeft / (contentWidth - containerWidth);
        const thumbPosition = scrollRatio * (100 - thumbWidth);

        setThumbWidth(thumbWidth);
        setThumbPosition(thumbPosition);
    }

    /**
     * @param {MouseEvent} e
     */
    const handleMouseDown = (e) => {
        e.preventDefault();
        if(!isEnabled) return;

        setIsDragging(true);
        setStartDragX(e.clientX);
        contentRef.current.classList.add('no-user-select');
    }

    const handleTouchStart = (e) => {
        if (!isEnabled) return;

        setIsDragging(true);
        setStartDragX(e.touches[0].clientX);
        contentRef.current.classList.add('no-user-select');
    };

    const handleTouchMove = (e) => {
        if (!isDragging || !isEnabled) return;

        const dx = e.touches[0].clientX - startDragX;
        const containerWidth = containerRef.current.offsetWidth;
        const contentWidth = contentRef.current.scrollWidth;
        const scrollDistance = dx * (contentWidth / containerWidth);

        containerRef.current.scrollLeft += scrollDistance;
        calculateThumbWidthAndPosition();
        setStartDragX(e.touches[0].clientX);
    };

    /**
     * @param {MouseEvent} e
     */
    const handleMouseMove = (e) => {
        if (!isDragging || !isEnabled) return;

        const dx = e.clientX - startDragX;
        const containerWidth = containerRef.current.offsetWidth;
        const contentWidth = contentRef.current.scrollWidth;
        const scrollDistance = dx * (contentWidth / containerWidth);

        containerRef.current.scrollLeft += scrollDistance;
        calculateThumbWidthAndPosition();
        setStartDragX(e.clientX);
    };

    /**
     * @param {MouseEvent} e
     */
    const handleTrackClick = (e) => {
        if (!thumbRef.current) return;

        const trackBounds = e.target.getBoundingClientRect();
        const thumbBounds = thumbRef.current.getBoundingClientRect();
        const clickPosition = e.clientX - trackBounds.left;

        if (e.clientX >= thumbBounds.left && e.clientX <= thumbBounds.right) {
            return;
        }

        const trackLength = trackBounds.width;
        const clickRatio = clickPosition / trackLength;
        const contentWidth = containerRef.current.scrollWidth;
        const containerWidth = containerRef.current.clientWidth;
        const maxScrollLeft = contentWidth - containerWidth;

        containerRef.current.scrollLeft = clickRatio * maxScrollLeft;
    };

    const handleMouseUp = () => {
        if(!isEnabled) return;

        setIsDragging(false);
        contentRef.current.classList.remove('no-user-select');
    };

    useEffect(() => {
        const container = containerRef.current;
        const handleScroll = () => {
            if (!container) return;
            setScrollLeft(container.scrollLeft);
        };
        const handleResize = () => {
            let enabled = typeof containerRef.current !== 'undefined' && typeof contentRef.current !== 'undefined' && containerRef.current !== null && contentRef.current !== null;
            setIsEnabled(enabled);

            if(!enabled) return;
            calculateThumbWidthAndPosition();
        };

        container.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current)
            }

            resizeTimeoutRef.current = setTimeout(handleResize, 100);
        });

        calculateThumbWidthAndPosition();

        return () => {
            container.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            clearTimeout(resizeTimeoutRef.current);
        }
    }, []);

    useEffect(() => {
        calculateThumbWidthAndPosition();
    }, [scrollLeft]);

    useEffect(() => {
        let enabled = typeof containerRef.current !== 'undefined' && typeof contentRef.current !== 'undefined' && containerRef.current !== null && contentRef.current !== null;
        setIsEnabled(enabled);
    }, [containerRef.current && contentRef.current]);

    useEffect(() => {
        if (!isEnabled) return;
        calculateThumbWidthAndPosition();
    }, [isEnabled]);

    useEffect(() => {
        const handleMouseUpGlobal = () => {
            if (isDragging) {
                handleMouseUp();
            }
        };

        const handleMouseMoveGlobal = (e) => {
            handleMouseMove(e);
        };

        if (isDragging) {
            document.addEventListener('mouseup', handleMouseUpGlobal);
            document.addEventListener('mousemove', handleMouseMoveGlobal);
        }

        return () => {
            document.removeEventListener('mouseup', handleMouseUpGlobal);
            document.removeEventListener('mousemove', handleMouseMoveGlobal);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    useEffect(() => {
        const handleTouchEndGlobal = () => {
            if (isDragging) {
                handleMouseUp();
            }
        };

        const handleTouchMoveGlobal = (e) => {
            handleTouchMove(e);
        };

        if (isDragging) {
            document.addEventListener('touchend', handleTouchEndGlobal);
            document.addEventListener('touchmove', handleTouchMoveGlobal, { passive: false }); // Use passive: false to allow preventDefault
        }

        return () => {
            document.removeEventListener('touchend', handleTouchEndGlobal);
            document.removeEventListener('touchmove', handleTouchMoveGlobal);
        };
    }, [isDragging, handleTouchMove]);

    return (
        <div className={'custom-scrollbar' + (!isEnabled ? ' custom-scrollbar--hidden' : '')}
             onClick={handleTrackClick}>
            <div
                className="custom-scrollbar__thumb"
                style={{width: `${thumbWidth}%`, left: `${thumbPosition}%`}}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart} // Listen to touch start
                ref={thumbRef}
                onClick={e => {
                    e.stopPropagation()
                    e.preventDefault();
                }}
            ></div>
        </div>
    );
}

export default CustomScrollbar;